/** URL search params of the dashboard, kept apart from the page so the router does not load the chart bundle. */
export const VIEWS = [
  ['revenue', 'Revenue'],
  ['units', 'Units and returns'],
  ['geography', 'Countries and platforms'],
  ['discounts', 'Discounts and sales'],
  ['microtransactions', 'Microtransactions and market fees'],
  ['wishlists', 'Wishlists'],
  ['payouts', 'Payouts'],
] as const;
export type View = (typeof VIEWS)[number][0];

export interface DashboardSearch {
  view: View;
  from: string;
  to: string;
  gameId?: string | undefined;
  partnerId?: string | undefined;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const today = () => new Date().toISOString().slice(0, 10);
export const monthStart = (monthsBack: number) => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - monthsBack, 1)).toISOString().slice(0, 10);
};

export type DashboardSearchParams = Partial<DashboardSearch>;

/** Filters live in the URL, so a view is shareable; invalid values are dropped. */
export function parseDashboardSearch(raw: Record<string, unknown>): DashboardSearchParams {
  const ok = (v: unknown, re: RegExp): v is string => typeof v === 'string' && re.test(v);
  return {
    ...(VIEWS.some(([v]) => v === raw.view) ? { view: raw.view as View } : {}),
    ...(ok(raw.from, ISO) ? { from: raw.from } : {}),
    ...(ok(raw.to, ISO) ? { to: raw.to } : {}),
    ...(ok(raw.gameId, UUID) ? { gameId: raw.gameId } : {}),
    ...(ok(raw.partnerId, UUID) ? { partnerId: raw.partnerId } : {}),
  };
}

/** Defaults for what the URL leaves out: revenue of the last 12 months. */
export const withDefaults = (p: DashboardSearchParams): DashboardSearch => ({ view: 'revenue', from: monthStart(11), to: today(), ...p });

export const analyticsQuery = (s: DashboardSearch) => {
  const q = new URLSearchParams({ from: s.from, to: s.to });
  if (s.gameId) q.set('gameId', s.gameId);
  if (s.partnerId) q.set('partnerId', s.partnerId);
  return q.toString();
};

